// Поле ввода с подписью, подсказкой и ошибкой. Близнец SelectField: тот же порядок частей,
// та же грамматика ошибки — чтобы форма читалась как одна форма, а не как склейка полей.
//
// Ошибка живёт ПОД полем и называет, что не так и как исправить. Красная рамка без текста —
// это молчание, по правилу 10 его не бывает.

import type { InputHTMLAttributes, ReactNode } from 'react';

import type { RemixiconComponentType } from '@remixicon/react';
import { RiErrorWarningLine } from '@remixicon/react';

import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';

export interface TextFieldProps {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Проверка по расфокусу: живая проверка дубля не должна дёргать сервер на каждой букве. */
  onBlur?: () => void;
  placeholder?: string;
  icon?: RemixiconComponentType;
  type?: InputHTMLAttributes<HTMLInputElement>['type'];
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  maxLength?: number;
  /** Что не так с введённым: причина и исправление, а не «неверное значение». */
  error?: string;
  /** Подсказка, когда ошибки нет: зачем поле и в каком виде его заполнять. */
  hint?: ReactNode;
}

export function TextField({
  id,
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  icon,
  type,
  inputMode,
  autoComplete,
  maxLength,
  error,
  hint,
}: TextFieldProps) {
  return (
    <div className='flex flex-col gap-1'>
      <Label.Root htmlFor={id}>
        {label}
        {required === true && <Label.Asterisk />}
      </Label.Root>
      <Input.Root size='small' hasError={error !== undefined}>
        <Input.Wrapper>
          {icon !== undefined && <Input.Icon as={icon} />}
          <Input.Input
            id={id}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            type={type}
            inputMode={inputMode}
            autoComplete={autoComplete}
            maxLength={maxLength}
            aria-invalid={error !== undefined}
          />
        </Input.Wrapper>
      </Input.Root>
      {error !== undefined ? (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {error}
        </Hint.Root>
      ) : (
        hint !== undefined && <Hint.Root>{hint}</Hint.Root>
      )}
    </div>
  );
}
